import { useState } from 'react';
import { useLocation } from 'react-router';
import { Bug, Lightbulb, MessageSquareWarning, HelpCircle } from 'lucide-react';
import { HelpBubble } from '@/help/HelpBubble';
import { FEEDBACK_KIND_LABEL, sendFeedback } from '@/data/feedback';
import type { FeedbackKind } from '@/domain/types';
import { Button, cn, Notice } from '@/ui/core';
import { TextArea } from '@/ui/form';
import { Dialog, useToast } from '@/ui/overlay';
import { useSession } from '@/app/providers';

const KINDS: { kind: FeedbackKind; sub: string; icon: typeof Bug }[] = [
  { kind: 'problem', sub: 'A glitch, an error, a wrong number', icon: Bug },
  { kind: 'confusing', sub: 'Hard to find or hard to understand', icon: HelpCircle },
  { kind: 'idea', sub: 'Something it could do better', icon: Lightbulb },
];

/**
 * "Tell us": a problem, something confusing, or an idea. Remembers which screen it was sent
 * from and the kind of device, so a glitch can be found again. Read in the manager area.
 */
export function FeedbackDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { pathname } = useLocation();
  const { mode } = useSession();
  const toast = useToast();
  const [kind, setKind] = useState<FeedbackKind>('problem');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const close = () => {
    setText('');
    setKind('problem');
    onClose();
  };
  const send = async () => {
    setBusy(true);
    try {
      await sendFeedback({ kind, text, page: pathname });
      toast.show('Thank you! It’s been sent.');
      close();
    } catch (err) {
      toast.error(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open={open}
      onClose={close}
      title={
        <span className="flex items-center gap-1">
          Report a problem or an idea <HelpBubble topic="feedback" size="sm" />
        </span>
      }
      footer={
        <>
          <Button onClick={close}>Cancel</Button>
          <Button variant="primary" busy={busy} disabled={text.trim().length < 3} onClick={send}>
            Send
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <div className="grid gap-2 sm:grid-cols-3">
          {KINDS.map((k) => (
            <button key={k.kind} type="button" aria-pressed={kind === k.kind} onClick={() => setKind(k.kind)} className={cn('flex min-h-16 items-center gap-2 rounded-2xl border-2 p-2.5 text-left', kind === k.kind ? 'border-accent bg-accent-soft' : 'border-border bg-surface hover:border-accent')}>
              <k.icon className="size-6 shrink-0 text-accent" aria-hidden />
              <span>
                <span className="block font-bold leading-tight">{FEEDBACK_KIND_LABEL[k.kind]}</span>
                <span className="block text-xs text-muted">{k.sub}</span>
              </span>
            </button>
          ))}
        </div>
        <label htmlFor="fb-text" className="text-sm font-semibold">
          {kind === 'problem' ? 'What happened? What did you tap, and what did you expect?' : kind === 'confusing' ? 'What was hard to find or understand?' : 'What would make it better?'}
        </label>
        <TextArea id="fb-text" value={text} onChange={(e) => setText(e.target.value)} rows={5} maxLength={2000} placeholder={kind === 'problem' ? 'e.g. I tapped Save on Brought from downstairs and nothing happened' : kind === 'idea' ? 'e.g. It would help to see last night’s number next to each drink' : 'e.g. I couldn’t find where to undo a spill'} />
        <p className="flex items-center gap-1.5 text-sm text-muted">
          <MessageSquareWarning className="size-4 shrink-0" aria-hidden /> Your name, this screen and the kind of device are sent with it, so it can be looked into.
        </p>
        {mode === 'practice' && <Notice tone="warn">You’re in Practice: this stays on this device only. To reach the manager area on other devices, send it from the real counts.</Notice>}
      </div>
    </Dialog>
  );
}
